/**
 * Resolves $ref chains in domain/model property maps (module://, shared://, #/$defs).
 */

import type { FormLoaderContext } from './types';
import { parseRef, resolveRef } from './uriResolver';

function localDocForRef(
  ref: string,
  ctx: FormLoaderContext,
  localDoc?: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (ref.startsWith('module://')) {
    const parsed = parseRef(ref);
    return ctx.moduleDomain.get(parsed.path) as Record<string, unknown> | undefined;
  }
  return localDoc;
}

export function resolvePropertyWithRefs(
  entry: Record<string, unknown>,
  ctx: FormLoaderContext,
  localDoc?: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  if (depth > 20) return entry;
  if (!('$ref' in entry) || typeof entry.$ref !== 'string') {
    return entry;
  }

  const ref = entry.$ref as string;
  const doc = localDocForRef(ref, ctx, localDoc) ?? localDoc;

  try {
    const resolved = resolveRef(ref, ctx, doc);
    if (resolved && typeof resolved === 'object' && !Array.isArray(resolved)) {
      const { $ref: _, ...overrides } = entry;
      const base = resolved as Record<string, unknown>;
      const merged = { ...base, ...overrides };

      // Deep-merge `properties`: per-field merge so $ref extensions can add or augment fields.
      // Override fields are merged INTO base fields (preserving base metadata), not replacing them.
      if (
        base.properties && typeof base.properties === 'object' && !Array.isArray(base.properties) &&
        overrides.properties && typeof overrides.properties === 'object' && !Array.isArray(overrides.properties)
      ) {
        const baseProps = base.properties as Record<string, Record<string, unknown>>;
        const overrideProps = overrides.properties as Record<string, Record<string, unknown>>;
        const mergedProps: Record<string, unknown> = { ...baseProps };
        for (const [field, fieldOverrides] of Object.entries(overrideProps)) {
          const baseField = baseProps[field];
          mergedProps[field] = baseField
            ? { ...baseField, ...fieldOverrides }
            : fieldOverrides;
        }
        merged.properties = mergedProps;
      }

      // Deep-merge `items.properties`: model.json can override per-field behavioral keys
      // (e.g. required, readOnlyWhen) on array item fields defined in the base domain.
      if (
        base.items && typeof base.items === 'object' && !Array.isArray(base.items) &&
        overrides.items && typeof overrides.items === 'object' && !Array.isArray(overrides.items)
      ) {
        const baseItems = base.items as Record<string, unknown>;
        const overrideItems = overrides.items as Record<string, unknown>;
        const baseItemProps = baseItems.properties as Record<string, Record<string, unknown>> | undefined;
        const overrideItemProps = overrideItems.properties as Record<string, Record<string, unknown>> | undefined;

        if (baseItemProps && overrideItemProps) {
          const mergedItemProps: Record<string, unknown> = { ...baseItemProps };
          for (const [field, fieldOverrides] of Object.entries(overrideItemProps)) {
            const baseField = baseItemProps[field];
            mergedItemProps[field] = baseField
              ? { ...baseField, ...fieldOverrides }
              : fieldOverrides;
          }
          merged.items = {
            ...baseItems,
            ...overrideItems,
            properties: mergedItemProps,
          };
        } else {
          merged.items = { ...baseItems, ...overrideItems };
        }
      }

      if ('$ref' in merged && typeof merged.$ref === 'string') {
        const nextDoc = localDocForRef(merged.$ref as string, ctx, doc) ?? doc;
        return resolvePropertyWithRefs(merged, ctx, nextDoc, depth + 1);
      }
      return merged;
    }
  } catch {
    // Resolution failed — keep raw entry; buildFieldMapping may surface a warning.
  }

  return entry;
}

export function resolveDomainRefs(
  properties: Record<string, unknown>,
  ctx: FormLoaderContext,
  localDoc?: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value && typeof value === 'object' && '$ref' in value) {
      const resolved = resolvePropertyWithRefs(value as Record<string, unknown>, ctx, localDoc);
      // After resolving a top-level $ref, the result may be an array with items.$ref
      // (e.g. espp.purchasePeriods resolved via module://espp, items still has module://purchase-period).
      // Recurse into items to complete the chain.
      if (
        resolved.type === 'array'
        && resolved.items && typeof resolved.items === 'object' && !Array.isArray(resolved.items)
      ) {
        const items = resolved.items as Record<string, unknown>;
        if ('$ref' in items) {
          const resolvedItems = resolvePropertyWithRefs(items, ctx, localDoc);
          if (
            resolvedItems.type === 'object'
            && resolvedItems.properties
            && typeof resolvedItems.properties === 'object'
          ) {
            result[key] = {
              ...resolved,
              items: {
                ...resolvedItems,
                properties: resolveDomainRefs(
                  resolvedItems.properties as Record<string, unknown>,
                  ctx,
                  localDoc,
                ),
              },
            };
          } else {
            result[key] = { ...resolved, items: resolvedItems };
          }
        } else if (items.type === 'object' && items.properties && typeof items.properties === 'object') {
          result[key] = {
            ...resolved,
            items: {
              ...items,
              properties: resolveDomainRefs(items.properties as Record<string, unknown>, ctx, localDoc),
            },
          };
        } else {
          result[key] = resolved;
        }
      } else {
        result[key] = resolved;
      }
      continue;
    }
    if (value && typeof value === 'object' && !('$ref' in value)) {
      const obj = value as Record<string, unknown>;
      if (obj.type === 'object' && obj.properties && typeof obj.properties === 'object') {
        result[key] = {
          ...obj,
          properties: resolveDomainRefs(obj.properties as Record<string, unknown>, ctx, localDoc),
        };
        continue;
      }
      if (obj.type === 'array' && obj.items && typeof obj.items === 'object') {
        const items = obj.items as Record<string, unknown>;
        if ('$ref' in items) {
          const resolvedItems = resolvePropertyWithRefs(items, ctx, localDoc);
          if (
            resolvedItems.type === 'object'
            && resolvedItems.properties
            && typeof resolvedItems.properties === 'object'
          ) {
            result[key] = {
              ...obj,
              items: {
                ...resolvedItems,
                properties: resolveDomainRefs(
                  resolvedItems.properties as Record<string, unknown>,
                  ctx,
                  localDoc,
                ),
              },
            };
          } else {
            result[key] = { ...obj, items: resolvedItems };
          }
          continue;
        }
        if (items.type === 'object' && items.properties && typeof items.properties === 'object') {
          result[key] = {
            ...obj,
            items: {
              ...items,
              properties: resolveDomainRefs(items.properties as Record<string, unknown>, ctx, localDoc),
            },
          };
          continue;
        }
      }
    }
    result[key] = value;
  }
  return result;
}
